/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

/**
 * Persistent banner that surfaces a partial or in-progress import
 * even if the user closed the tab mid-upload.
 *
 * Consumes GET /api/network/import-status. Renders when status is
 * 'partial' or 'in_progress'. Silent when null or 'complete'.
 *
 * Fixes the "system knows something is wrong and does not say so"
 * gap flagged in the plan review.
 */

import React from 'react';

export default function ImportStatusBanner({ status, onRetry }) {
  if (!status || status.status === 'complete') return null;

  const { status: s, rowsExpected, rowsWritten, failureReason } = status;

  if (s === 'in_progress') {
    return (
      <div style={{ ...bannerStyle, borderLeft: '4px solid #f59e0b' }}>
        <div style={{ fontWeight: 600 }}>Your last import may not have finished.</div>
        <div style={{ fontSize: '13px', color: '#475569', marginTop: '4px' }}>
          The system recorded {rowsWritten.toLocaleString()} of {rowsExpected.toLocaleString()} connections
          before it lost contact. Retry to complete.
        </div>
        <div style={{ marginTop: '10px' }}>
          <button type="button" onClick={onRetry} style={retryBtnStyle}>Retry import</button>
        </div>
      </div>
    );
  }

  // status === 'partial'
  return (
    <div style={{ ...bannerStyle, borderLeft: '4px solid #dc2626' }}>
      <div style={{ fontWeight: 600 }}>
        Your last import did not finish — {rowsWritten.toLocaleString()} of {rowsExpected.toLocaleString()} connections imported.
      </div>
      <div style={{ fontSize: '13px', color: '#475569', marginTop: '4px' }}>
        Re-import to complete. The import is idempotent — old data will be replaced.
      </div>
      {failureReason && (
        <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '6px' }}>
          Reason: {failureReason}
        </div>
      )}
      <div style={{ marginTop: '10px' }}>
        <button type="button" onClick={onRetry} style={retryBtnStyle}>Re-import now</button>
      </div>
    </div>
  );
}

const bannerStyle = {
  background: '#fefce8',
  padding: '16px',
  borderRadius: '6px',
  marginBottom: '16px'
};

const retryBtnStyle = {
  padding: '8px 16px',
  background: '#00BCD4',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer'
};
